// START IMPORTS
// Hooks imports
import { useReport } from '@ltvco/refresh-lib/hooks';
// Theme and UI imports
import { Box, Stack } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  nameize,
  DateUtil,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  DebugMenu,
  flattenBonusData,
  Owner,
  PersonReport as PersonReportType,
  ReportChangesOverview,
  ReportFactory,
  ReportLoading,
  ReportMonitor,
  ReportOptions,
  useSession,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  type ReportNavigationMenuProps,
  EmailSection as EmailSectionV2,
  PhoneSection as PhoneSectionV2,
  AddressHistorySection as AddressHistorySectionV2,
  NeighborsSection as NeighborsSectionV2,
  RelativesSection as RelativesSectionV2,
  AssociatesSection as AssociatesSectionV2,
  EducationSection as EducationSectionV2,
  JobsSection as JobsSectionV2,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  PersonReportOverview,
  ReportActionsWithDateUpdated,
  ReportRating as ReportRatingV2,
  NameAncestrySection,
  NotesSection as NotesSectionV2,
  MaritalRecordsSection as MaritalRecordsSectionV2,
  AssetsSection,
  CriminalOrTrafficSection as CriminalOrTrafficSectionV2,
  BankruptcySection as BankruptcySectionV2,
  CourtRecords,
  CourtRecordsLoadingScreen,
  RelatedReportsSection as RelatedReportsSectionV2,
  LicensesAndPermitsSection as LicensesAndPermitsSectionV2,
  ReportClaimingSection,
  SocialSectionV2,
  ReportNullState,
  PdfSection,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useFeatureIsOn, useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import {
  getPersonNavLinkData,
  getPersonNavLinkDataV2,
} from 'navLinkData/personNavLinkData';
import { getClaimedReports } from 'utils/getClaimedReports';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface PersonReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'person';
// END CONSTANTS

export function PersonReport({ permalink }: PersonReportProps) {
  // START HOOKS_AND_QUERIES
  const {
    session: { account },
  } = useSession();
  const queryClient = useQueryClient();

  const queryResult = useReport(permalink);
  const { currentMonitors, reportIsMonitored } = queryResult;

  const currentUserInfo = account?.account?.user_info;

  const zeroed = isZeroed(queryResult);
  const { hash } = useLocation();
  const isInSuggestedEmailVerification = useFeatureIsOn('oar-1146');
  const isInPartialAddressesVariation = useFeatureIsOn('OAR-1579');
  const alternativeTitle = isInPartialAddressesVariation
    ? 'Possible Locations'
    : undefined;
  const customTimeDelay = isInSuggestedEmailVerification ? 200 : undefined;
  useScrollToSectionOnNavigate(hash, queryResult, customTimeDelay);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [showBankruptcyData] = useState<boolean>(false);
  const [showCriminalData, setShowCriminalData] = useState(false);
  const [isCourtRecordsLoading, setIsCourtRecordsLoading] = useState(false);

  const [reportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });

  const [activeClaiming, setActiveClaiming] = useState(false);
  const [disableClaiming, setDisableClaiming] = useState(false);
  const [claimedReportPermalink, setClaimedReportPermalink] =
    useState<string>('');

  const [report, setReport] = useState<PersonReportType | null>(null);
  const [, setReportBvId] = useState<string>('');
  const [reportOwner, setReportOwner] = useState<Owner | null>(null);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const hideCriminalBJLData = account?.account.user_settings
    .hide_criminal_records as boolean;
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    const isAnotherReportClaimed = currentMonitors?.some(
      (monitor: ReportMonitor) => {
        return (
          monitor.report_type === 'detailed_person_report' &&
          monitor.is_claimed &&
          permalink !== monitor.permalink
        );
      }
    );
    setDisableClaiming(isAnotherReportClaimed);
    setActiveClaiming(
      currentMonitors?.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [currentMonitors, permalink]);

  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      queryClient.invalidateQueries(['request', 'bbq']);

      if (queryResult?.data?.entities) {
        setReport(ReportFactory.create(queryResult?.data, 'person'));
      }
    }
  }, [queryResult.data, queryResult.isSuccess, reportOptions, queryClient]);

  useEffect(() => {
    if (!currentMonitors) {
      return;
    }

    const REPORT_TYPE = 'detailed_person_report';
    const claimedReports = getClaimedReports(currentMonitors, REPORT_TYPE);

    if (claimedReports.length) {
      const mainPermalink = claimedReports[0].permalink;
      setClaimedReportPermalink(mainPermalink);
    } else {
      if (claimedReportPermalink !== '') {
        setClaimedReportPermalink('');
      }
    }
  }, [currentMonitors, claimedReportPermalink]);

  useEffect(() => {
    if (report) {
      const tempReportOwner = report.getOwner(reportOptions);
      if (tempReportOwner) {
        setReportOwner(tempReportOwner);
      }

      const queryParameters =
        report?.data?.meta?.request_info?.query_parameters;

      if (queryParameters) {
        const bvidObj = queryParameters.find(
          (param: { name: string }) => param.name === 'bvid'
        );
        setReportBvId(bvidObj?.['values']?.[0] || '');
      }
    }
  }, [report]);
  // END EFFECTS

  // START EVENT_HANDLERS
  const delayForCourtRecords = 1000;

  function handleCourtRecordsLoadingScreen() {
    setIsCourtRecordsLoading(true);
    queryClient.invalidateQueries({
      queryKey: ['report', permalink],
    });
    queryClient.refetchQueries({
      queryKey: ['report', permalink],
    });
    setTimeout(() => {
      setIsCourtRecordsLoading(false);
      setShowCriminalData(true);
    }, delayForCourtRecords * 7);
  }
  // END EVENT_HANDLERS

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={14} />;
  }

  if (zeroed) {
    return <ReportNullState />;
  }

  if (!reportOwner || !report) {
    return <ReportLoading menuItems={14} />;
  }

  if (!report?.data?.people?.length) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const reportUpgraded = queryResult.data?.meta?.report_upgraded;
  const revealFreeBonusData = showCriminalData && !isCourtRecordsLoading;

  let showReportClaiming = false;
  if (
    currentUserInfo?.first_name &&
    currentUserInfo.last_name &&
    report?.personName.includes(currentUserInfo.first_name) &&
    report?.personName.includes(currentUserInfo.last_name) &&
    !disableClaiming
  ) {
    showReportClaiming = true;
  }

  const MonitoringComponent = showReportClaiming && (
    <ReportClaimingSection
      reportPermaLink={permalink}
      reportType={reportType}
      reportIsMonitoredAndClaimed={activeClaiming}
    />
  );

  const {
    ownerName,
    nameInfo,
    emails,
    phones,
    educations,
    jobs,
    profiles,
    usernames,
    addresses,
    ownedAssets,
    marital,
    relatives,
    associates,
    neighbors,
    courts,
    bonusData,
  } = reportOwner as Owner;
  const flattenedBonusData = flattenBonusData(bonusData);

  const licensesCount =
    (bonusData?.licenses?.dea?.length ?? 0) +
    (bonusData?.licenses?.faa?.length ?? 0) +
    (bonusData?.licenses?.professional?.length ?? 0) +
    (bonusData?.licenses?.sporting?.length ?? 0) +
    (bonusData?.licenses?.weapon?.length ?? 0) +
    (bonusData?.filings?.business?.length ?? 0) +
    (bonusData?.filings?.ucc?.length ?? 0);

  const personNavLinkData = getPersonNavLinkData(
    reportOwner as Owner,
    showCriminalData,
    showBankruptcyData,
    hideCriminalBJLData,
    reportUpgraded ?? false,
    flattenedBonusData.length,
    relatives || []
  );

  const personNavLinkDataV2 = getPersonNavLinkDataV2(
    reportOwner as Owner,
    hideCriminalBJLData,
    revealFreeBonusData,
    relatives || [],
    licensesCount
  );

  const dob =
    reportOwner.identity?.dobs?.[0]?.date?.parsed?.year?.toString() ?? '';

  document.title = `${report.personName} - NeighborWho`;

  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';
  // END DATA_PREPARATION

  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        {/* START DESKTOP_NAVIGATION */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Person Report"
            headerTitle={ownerName}
            menuItems={
              personNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        {/* END DESKTOP_NAVIGATION */}

        {/* START MAIN_CONTENT */}
        <Box marginTop={0}>
          {/* START REPORT_CHANGES_OVERVIEW */}
          {reportIsMonitored && (
            <ReportChangesOverview
              permalink={permalink}
              navLinkData={personNavLinkData}
            />
          )}
          {/* END REPORT_CHANGES_OVERVIEW */}

          {/* START REPORT_ACTIONS */}
          <Box
            sx={{
              height: 30,
              backgroundColor: 'background.default',
              position: 'sticky',
              marginBottom: '-10px',
              top: 52,
              zIndex: 10,
              display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' },
            }}
          />

          <>
            <ReportActionsWithDateUpdated
              reportType={reportType}
              reportTitle="Person"
              dateUpdated={reportUpdateDate}
              reportUpgraded={reportUpgraded}
              revealFreeBonusData={revealFreeBonusData}
            />
            <PersonReportOverview
              reportType={reportType}
              owner={reportOwner as Owner}
              personName={ownerName}
              personOverviewItemsData={{
                hideCriminalBJLData,
                phones,
                emails,
                addresses,
                relatives,
                images: reportOwner!.images,
                profiles,
                usernames,
                jobs,
                educations,
              }}
              dateUpdated={reportUpdateDate}
              reportUpgraded={revealFreeBonusData}
            />
          </>
          {/* END REPORT_ACTIONS */}

          {/* START REPORT_OVERVIEW */}
          {/* END REPORT_OVERVIEW */}

          {MonitoringComponent}

          {/* START PDF_SECTION */}
          <PdfSection permalink={permalink} />
          {/* END PDF_SECTION */}

          {/* START SECTIONS */}
          <PhoneSectionV2
            personName={ownerName}
            phoneList={phones}
            permalink={permalink}
          />

          <EmailSectionV2
            personName={ownerName}
            emailList={emails}
            permalink={permalink}
          />

          <AddressHistorySectionV2
            addresses={addresses}
            personName={ownerName}
            permalink={permalink}
            alternateTitle={alternativeTitle}
          />

          <SocialSectionV2
            personName={ownerName}
            profiles={profiles}
            permalink={permalink}
          />

          <NameAncestrySection
            firstName={nameize(nameInfo?.parsed?.first ?? null)}
            surName={nameize(nameInfo?.parsed?.last ?? null)}
            yearOfBirth={dob}
          />

          <RelativesSectionV2
            personName={ownerName}
            relatives={relatives || []}
            permalink={permalink}
          />

          <MaritalRecordsSectionV2
            personName={ownerName}
            marital={marital}
            permalink={permalink}
          />

          <AssociatesSectionV2
            personName={ownerName}
            associates={associates || []}
            permalink={permalink}
          />

          <NeighborsSectionV2
            personName={ownerName}
            neighbors={neighbors || []}
            permalink={permalink}
          />

          {/* START COURT_RECORDS_SECTION */}
          {!hideCriminalBJLData && (
            <>
              {revealFreeBonusData ? (
                <>
                  <CriminalOrTrafficSectionV2
                    criminalOrTrafficData={courts.criminal || []}
                    personName={ownerName}
                  />
                  <BankruptcySectionV2
                    personName={ownerName}
                    bankruptcyData={courts.bankruptcy || []}
                  />
                </>
              ) : (
                <>
                  {isCourtRecordsLoading ? (
                    <CourtRecordsLoadingScreen
                      personName={ownerName}
                      delayDuration={delayForCourtRecords}
                    />
                  ) : (
                    <CourtRecords
                      handleCourtRecordsLoadingScreen={
                        handleCourtRecordsLoadingScreen
                      }
                    />
                  )}
                </>
              )}
            </>
          )}
          {/* END COURT_RECORDS_SECTION */}

          <JobsSectionV2
            personName={ownerName}
            jobs={jobs}
            permalink={permalink}
          />

          <EducationSectionV2
            personName={ownerName}
            educations={educations}
            permalink={permalink}
          />

          <LicensesAndPermitsSectionV2
            personName={ownerName}
            bonusData={bonusData}
            licensesAndPermitsCount={licensesCount}
          />

          <AssetsSection
            personName={ownerName}
            ownedAssets={ownedAssets}
            permalink={permalink}
          />

          <NotesSectionV2 permalink={permalink} />

          <RelatedReportsSectionV2
            reportType="person"
            personName={ownerName}
            potentialOwners={report.data.people}
            relatives={relatives}
            addresses={addresses}
            phones={phones}
            emails={emails}
            usernames={usernames}
          />

          <ReportRatingV2
            rating={report.data.meta?.rating || null}
            report_type={reportType}
          />
          {/* END SECTIONS */}
        </Box>
        {/* END MAIN_CONTENT */}
      </Stack>

      {/* START MOBILE_NAVIGATION */}
      <ReportNavigationMobileMenu
        reportType="Person Report"
        headerTitle={ownerName}
        menuItems={
          personNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
        }
      />
      {/* END MOBILE_NAVIGATION */}

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
    </Box>
  );
  // END RENDER
}
